import moment from 'moment'
import { db, exists, getUserId } from '@/firebaseApp'
import { Guest, GuestList, GuestState } from '@/services/Guests'
import accessCodes from './AccessCodes'
import { RoomsList } from './Rooms'

export type SeatingType = {
  id?: string
  expiresOn: number
  validFrom: number
  guests: GuestList
}

export type RoomState = {
  videoId?: string|null
  videoIsPlaying?: boolean
}


class Seatings {
  async create(restaurantId: string, date: Date) {
    while (true) {
      const accessCode = accessCodes.generate()
      const seatingRef = db.ref(`restaurants/${restaurantId}/seatings/${accessCode}`)
      if (await exists(seatingRef)) {
        continue
      }

      const validFrom = moment(date).set({hour: 6})
      const expiresOn = moment(validFrom).add(24, 'hours').subtract(1, 's')
      const seating = {
        validFrom: validFrom.valueOf(),
        expiresOn: expiresOn.valueOf(),
      }

      await seatingRef.set(seating)
      break
    }
  }

  async getAllSeatings(restaurantId: string, f: (seatings: SeatingType[]) => void) {
    const today = moment().startOf('d')
    return db
      .ref(`restaurants/${restaurantId}/seatings`)
      .startAt(today.toDate().getTime(), 'validFrom')
      .orderByChild('validFrom')
      .on('value', s => {
        const seatings: SeatingType[] = []
        s.forEach(data => {
          const seating: Partial<SeatingType> = data.val()
          seating.id = data.key + ''
          seatings.push(seating as SeatingType)
        })

        return f(seatings)
      })
  }

  async getActiveSeating(restaurantId: string) {
    const now = Date.now()
    const snapshot = await db.ref(`restaurants/${restaurantId}/seatings`).orderByChild('validFrom').once('value')
    const seatings = snapshot.val()

    // @ts-ignore
    const activeKey = Object.keys(seatings).find(key => {
      const s = seatings[key]
      // Make the new validFrom
      const validFrom = +moment(s.validFrom).set({hour: 6})
      // Make the new expiresOn
      const expiresOn = +moment(s.expiresOn).set({hour: 5, minute: 59, seconds: 59})
      return now >= validFrom && now <= expiresOn
    })

    if (activeKey) {
      const s = seatings[activeKey]
      s.id = activeKey
      return s
    }

    return null
  }

  async deleteSeating(restaurantId: string, seatingId: string) {
    return db.ref(`restaurants/${restaurantId}/seatings/${seatingId}`).remove()
  }

  async deleteOldSeatings(restaurantId: string) {
    const lastMonth = moment().add(-30, 'd').toDate().getTime()
    const snapshot = await db.ref(`restaurants/${restaurantId}/seatings`).once('value')
    const seatings = snapshot.val()

    Object.keys(seatings).forEach(key => {
      const seating: SeatingType = seatings[key]
      if (!seating) {
        return
      }

      if (seating.validFrom < lastMonth) {
        // Old than a month, remove
        this.deleteSeating(restaurantId, key)
      }
    })
  }

  public async observeGuest(restaurantId: string, accessCode: string, f: (guest: Guest) => void) {
    const uid = await getUserId()
    return db
      .ref(`restaurants/${restaurantId}/seatings/${accessCode}/guests/${uid}`)
      .on('value', s => f(s.val()))
  }

  public observeSeatingRoomState(restaurantId: string, accessCode: string, roomId: string, f: (roomState: RoomState) => void) {
    db.ref(`restaurants/${restaurantId}/seatings/${accessCode}/rooms/${roomId}`)
      .on('value', s => f(s.val()))
  }

  public async updateRoomState(restaurantId: string, accessCode: string, roomId: string, roomState: RoomState) {
    const ref = db.ref(`restaurants/${restaurantId}/seatings/${accessCode}/rooms/${roomId}`)
    await ref.set(roomState)
  }

  public async isLobbyFull(restaurantId: string, accessCode: string) {
    const guests = (await db.ref(`restaurants/${restaurantId}/seatings/${accessCode}/guests`).once('value')).val()
    const guestsInLobby = Object.keys(guests).filter(guestId => {
      const guest: Guest = guests[guestId];
      return !guest.room && guest.isActive && guest.state === GuestState.Welcome
    })
    return guestsInLobby.length >= 17
  }
}

export default new Seatings()
